import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Gera o PDF de um nó (orçamento/OS/laudo) de forma confiável no iPhone.
// react-to-print (print de iframe) e dom-to-image desalinham/apagam no iOS
// Safari (foreignObject bugado no WebKit mobile). html2canvas desenha o DOM
// direto num canvas (sem foreignObject), com texto E imagens no lugar certo.

const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;

// Pontos de quebra de pagina (em pt) A4 em A4, mas subindo pro topo de uma
// imagem se a quebra cairia no meio dela (evita foto cortada entre paginas).
const computePageBreaks = (node, contentHeightPt, cssToPt) => {
  const nodeTop = node.getBoundingClientRect().top;
  const bands = Array.from(
    node.querySelectorAll('.image-item, .os-term-report-image-item')
  ).map((el) => {
    const r = el.getBoundingClientRect();
    return { top: (r.top - nodeTop) * cssToPt, bottom: (r.bottom - nodeTop) * cssToPt };
  });

  const breaks = [0];
  while (breaks[breaks.length - 1] + A4_HEIGHT_PT < contentHeightPt) {
    const prev = breaks[breaks.length - 1];
    let next = prev + A4_HEIGHT_PT;
    bands.forEach((band) => {
      if (next > band.top && next < band.bottom && band.top > prev + 40) {
        next = Math.min(next, band.top);
      }
    });
    breaks.push(next);
  }
  breaks.push(contentHeightPt);
  return breaks;
};

export const buildPdfBlobFromNode = async (node) => {
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    imageTimeout: 15000,
    logging: false,
  });

  const cssToPt = A4_WIDTH_PT / node.offsetWidth;
  const contentHeightPt = node.offsetHeight * cssToPt;
  const ptToPx = canvas.height / contentHeightPt;
  const breaks = computePageBreaks(node, contentHeightPt, cssToPt);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [A4_WIDTH_PT, A4_HEIGHT_PT],
  });
  for (let i = 0; i < breaks.length - 1; i += 1) {
    const heightPt = breaks[i + 1] - breaks[i];
    const sliceTopPx = Math.round(breaks[i] * ptToPx);
    const sliceHeightPx = Math.round(heightPt * ptToPx);
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceHeightPx;
    slice
      .getContext('2d')
      .drawImage(canvas, 0, sliceTopPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
    if (i > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, A4_WIDTH_PT, heightPt);
  }

  return pdf.output('blob');
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Entrega o PDF: folha de compartilhar nativa no iOS (de onde da pra
// salvar/imprimir/enviar); download direto onde nao ha Web Share de arquivo.
export const deliverPdfBlob = async (blob, filename) => {
  const file = new File([blob], filename, { type: 'application/pdf' });

  const canShareFile =
    typeof navigator !== 'undefined' &&
    navigator.canShare &&
    navigator.canShare({ files: [file] });

  if (canShareFile) {
    try {
      await navigator.share({ title: filename, files: [file] });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
  }
  downloadBlob(blob, filename);
};

export const sharePdfFromNode = async (node, filename) => {
  const blob = await buildPdfBlobFromNode(node);
  await deliverPdfBlob(blob, filename);
};
